import { METRIC_FORMAT, type POSSIBLE_METRICS_ENUM } from "constants/metrics"
import { formatters } from "@tokenterminal/core/utils/sharedFormatter"
import { type MetricConfiguration } from "@tokenterminal/tt-analytics-api-types/dist/api/metrics"
import { Box } from "@tokenterminal/ui/Box"
import { SERIE_FORMAT } from "@tokenterminal/ui/Chart/ChartOptions"
import { UnitText } from "@tokenterminal/ui/Table"
import { colorsV4 } from "@tokenterminal/ui/theme/themeV4/colorsContract.css"
import { reverseNumeral } from "@tokenterminal/ui/utils/numerals"
import { monospaceFontCss } from "@tokenterminal/ui/utils/typography.css"
import { MetricNotAvailable } from "components/terminal/MetricNotAvailable/MetricNotAvailable"
import { useNumberFormatter } from "hooks/useNumberFormatter"
import { getMetricLabel } from "modules/metrics/getMetricLabel"
import { useMetricsMeta } from "modules/metrics/hooks/useMeta"
import { formatNumber } from "modules/screener/utils/cellFormatHelper"
import { type ProjectType } from "types/ApiTypes"
import { capitalize } from "utils/lodash/capitalize"
import { groupBy } from "utils/lodash/groupBy"
import {
  tableCellBase,
  tableDefinitionCell,
  tableSection,
  tableValueCell,
} from "./FinancialStatement.css"
import {
  type FinancialStatementDataType,
  ITEMS_PER_PAGE,
  metricsWithIndent,
  METRICS_BY_SECTION,
  shouldMetricBeParenthesized,
} from "./FinancialStatement.utils"
import {
  ratioValueContainerStyle,
  ratioValueStyle,
} from "./FinancialStatementSection.css"

const NotAvailable = ({
  project,
  metric,
}: {
  project: ProjectType
  metric: string
}) => (
  <MetricNotAvailable
    project={project}
    metricName={metric}
    color="neutral"
    iconColor="inherit"
    fontSize="xs"
    className={monospaceFontCss}
  />
)

const formatValue = ({
  value,
  project,
  metricConfiguration,
  format,
  isPlainValue,
  numberFormat,
}: {
  value: number | null
  project: ProjectType
  metricConfiguration: MetricConfiguration
  format?: `${SERIE_FORMAT}`
  /** For percentage value cells dont show +/- or color the result as red/green  */
  isPlainValue?: boolean
  numberFormat?: string
}) => {
  if (value === null)
    return (
      <NotAvailable project={project} metric={metricConfiguration.metric_id} />
    )

  switch (format) {
    case METRIC_FORMAT.RATIO:
      if (Number(value) > 50000) {
        // hide ratio values exceeding 50k because they are most likely wrong
        return (
          <NotAvailable
            project={project}
            metric={metricConfiguration.metric_id}
          />
        )
      }
      const ratioText = formatters.ratio({
        value: Number(value),
      })

      return (
        <Box className={ratioValueContainerStyle}>
          <Box as="span" className={ratioValueStyle}>
            {ratioText.replace("x", "")}
          </Box>
          <UnitText data-unit-type="ratio" style={{ fontSize: "16px" }}>
            ×
          </UnitText>
        </Box>
      )

    case METRIC_FORMAT.NUMBER:
      return <span>{formatNumber(Number(value), numberFormat)}</span>

    case METRIC_FORMAT.PERCENTAGE:
      const percentage = Math.abs(value) * 100
      if (isPlainValue) {
        // For plain value cells dont show +/- or color the result as red/green
        return formatters.percent({ value: percentage, format: "0,0.00" })
      }
      const colorClassName =
        (metricConfiguration.format === "ratio"
          ? reverseNumeral(value)
          : value) > 0
          ? "text-fg-emerald"
          : "text-fg-red"

      return (
        <span className={colorClassName}>
          {formatters.percent({ value: percentage })}
        </span>
      )

    default:
      // default to format === price
      return (
        <div>
          <UnitText>$</UnitText>
          <span>{formatNumber(Number(value), numberFormat)}</span>
        </div>
      )
  }
}

type FinancialStatementSectionProps = {
  name: string
  length: number // length of items in header used to fill metrics which are not in data
  data: FinancialStatementDataType[]
  showDefinitions: boolean
  showChanges: boolean
  project: ProjectType
  page?: number // used for PDF part for splitting data into multiple pages
}

const FinancialStatementSection = ({
  name,
  length,
  data,
  showDefinitions,
  showChanges,
  project,
  page,
}: FinancialStatementSectionProps) => {
  const groupedData = groupBy(data, "metric_id")
  const numberFormat = useNumberFormatter()

  const projectMetrics = METRICS_BY_SECTION[name].filter(
    (metric) => typeof project.metric_availability[metric] === "boolean"
  ) as POSSIBLE_METRICS_ENUM[]
  const metrics = useMetricsMeta()

  if (projectMetrics.length === 0) return null

  return (
    <Box
      className={tableSection}
      display="flex"
      flexDirection="column"
      width="fit-content"
      height="100%"
      style={{
        borderBottom: "1px solid",
        borderBottomColor: colorsV4.border.default,
      }}
    >
      <div className="flex gap-0 items-center justify-start text-xs font-bold text-fg-default">
        <Box className={tableCellBase}>{name}</Box>
        {showDefinitions && (
          <Box className={tableDefinitionCell}>Definition</Box>
        )}
      </div>
      {projectMetrics.map((metric: POSSIBLE_METRICS_ENUM) => {
        const data =
          groupedData[metric] ||
          Array(length).fill({ value: null, pop_change: null })

        const metricConfig = metrics.find(
          (config) => config.metric_id === metric
        )!

        const definition = capitalize(
          (data[0]?.definition || metricConfig.definition) ?? ""
        )

        const dataToUse =
          typeof page === "number"
            ? data.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE)
            : data

        const metricTitle = shouldMetricBeParenthesized(metric)
          ? `(${getMetricLabel(metricConfig, project)})`
          : getMetricLabel(metricConfig, project)

        return (
          <div key={metric} className="flex gap-0 items-stretch justify-start">
            <Box className={tableCellBase} data-metric={metric}>
              <div
                className={`text-xs ${metric === "earnings" ? "text-fg-primary" : "text-fg-secondary"} ${metricsWithIndent.includes(metric) ? "ml-3" : ""}`}
              >
                {metricTitle}
              </div>
            </Box>
            {showDefinitions && (
              <Box className={tableDefinitionCell} data-metric={metric}>
                <p className="text-xs text-fg-secondary">{definition}</p>
              </Box>
            )}
            {dataToUse.map((d, i: number) => (
              <Box
                key={metric + i}
                display="flex"
                gap="2x"
                data-show-changes={showChanges}
                data-metric={metric}
                className={tableValueCell}
                fontSize="12"
              >
                {formatValue({
                  value: d.value,
                  project,
                  metricConfiguration: metricConfig,
                  format: metricConfig.format as `${SERIE_FORMAT}`,
                  isPlainValue: true,
                  numberFormat,
                })}
                {showChanges ? (
                  <div className="min-w-[50px]">
                    {formatValue({
                      value: d.pop_change,
                      project,
                      metricConfiguration: metricConfig,
                      format: SERIE_FORMAT.PERCENTAGE,
                    })}
                  </div>
                ) : null}
              </Box>
            ))}
          </div>
        )
      })}
    </Box>
  )
}

export default FinancialStatementSection
